import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Camera, VideoOutput } from '@/data/cameras';

interface DbCamera {
  id: string;
  name: string;
  brand: string;
  type: 'cinema' | 'mirrorless' | 'broadcast';
  sensor: string;
  weight: number;
  dimensions: { width: number; height: number; depth: number };
  ports: string[];
  video_outputs: VideoOutput[];
  power_input: string[];
  lens_mount: string;
  cooling_type: string;
  image: string | null;
  is_active: boolean;
  power_draw: number | null;
}

// Transform database camera to Camera format
const transformCamera = (db: DbCamera): Camera => ({
  id: db.id,
  name: db.name,
  brand: db.brand,
  type: db.type,
  sensor: db.sensor,
  weight: db.weight,
  dimensions: db.dimensions,
  ports: db.ports,
  videoOutputs: db.video_outputs,
  powerInput: db.power_input,
  lensMount: db.lens_mount,
  coolingType: db.cooling_type as 'active' | 'passive' | 'none',
  image: db.image || undefined,
  powerDraw: db.power_draw || undefined,
});

export function useCameras(options?: { demoOnly?: boolean }) {
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchCameras = async () => {
      setIsLoading(true);
      try {
        let query = supabase
          .from('cameras')
          .select('*')
          .eq('is_active', true);

        // Filter by demo status if requested
        if (options?.demoOnly) {
          query = (query as any).eq('is_demo', true);
        }

        const { data, error: fetchError } = await query;

        if (fetchError) throw fetchError;

        if (data) {
          setCameras(data.map(c => transformCamera(c as unknown as DbCamera)));
        }
      } catch (err) {
        console.error('Error fetching cameras:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch cameras'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchCameras();
  }, [options?.demoOnly]);

  const getCameraById = useCallback((id: string): Camera | undefined => {
    return cameras.find(camera => camera.id === id);
  }, [cameras]);

  const getCamerasByBrand = useCallback((brand: string): Camera[] => {
    return cameras.filter(camera => camera.brand === brand);
  }, [cameras]);

  return {
    cameras,
    isLoading,
    error,
    getCameraById,
    getCamerasByBrand,
  };
}
